import { Badge, Group, Popover, ScrollArea, Stack, Text, Tooltip, UnstyledButton} from "@mantine/core";
import { CaretRightIcon, CaretDownIcon, WarningCircleIcon} from "@phosphor-icons/react";
import {cloneElement, isValidElement, type CSSProperties, type ReactElement} from "react";
import Icon from "../../../components/Icon/Icon.tsx";
import IconMap from "../../../components/Icon/IconMap.ts";
import classes from "./ProductionModuleTimelinePage.module.css";
import {GanttGroupSection, GanttRow, GanttSegment, MissingMaterialsQueueItemDetail, TimelineScope} from "./ProductionModuleTimeline.types.ts";
import {useDisclosure} from "@mantine/hooks";

interface TimelineBand {
    key: string;
    left: number;
    width: number;
    label: string;
    mobileLabel: string;
}

interface TimelineDivider {
    key: string;
    left: number;
}

interface ProductionModuleTimelineViewProps {
    groupedVisibleRows: GanttGroupSection[];
    collapsedGroupSet: Set<string>;
    isMobile: boolean;
    monthBands: TimelineBand[];
    monthDividers: TimelineDivider[];
    gridTicks: TimelineDivider[];
    weekDividers: TimelineDivider[];
    windowStart: number;
    windowEnd: number;
    windowSeconds: number;
    gridTemplateColumns: string;
    labelColumnWidth: number;
    mobileTimelineWidth: number;
    onToggleGroup: (groupKey: string) => void;
    onOpenMaterialsDetail: (row: GanttRow) => void;
}

function cssVars(vars: Record<string, string | number>): CSSProperties {
    return vars as CSSProperties;
}

function getRowLaneCount(row: GanttRow): number {
    return Math.max(1, row.segments.reduce((max, segment) => Math.max(max, segment.lane + 1), 1));
}

function getRowHeight(row: GanttRow): number {
    const laneCount = getRowLaneCount(row);
    const baseHeight = Math.max(74, 30 + Math.max(0, laneCount - 1) * 22);
    const hasMaterialsDeadline = row.materialsStatus.details.some((detail) => detail.requiredByAt !== null);
    return hasMaterialsDeadline ? baseHeight + 22 : baseHeight;
}

function SegmentPopover({segment, children}: {segment: GanttSegment; children: ReactElement}) {
    const [opened, {open, close}] = useDisclosure()
    if (!isValidElement(children)) {
        return children;
    }

    const target = cloneElement(children, {
        onMouseEnter: open,
        onMouseLeave: close,
    });

    return (
        <Popover
            opened={opened}
            position="top"
            withArrow
            shadow="md"
            withinPortal
            zIndex={400}
        >
            <Popover.Target>
                {target}
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap={2}>
                    {segment.details.map((line, index) => (
                        <Text key={`${segment.key}:detail:${index}`} size="xs">
                            {line}
                        </Text>
                    ))}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}

function MaterialsDeadlinePopover({
    detail,
    row,
    children,
    onOpenMaterialsDetail,
}: {
    detail: MissingMaterialsQueueItemDetail;
    row: GanttRow;
    children: ReactElement;
    onOpenMaterialsDetail: (row: GanttRow) => void;
}) {
    const [opened, {open, close}] = useDisclosure();

    if (!isValidElement(children)) {
        return children;
    }

    const target = cloneElement(children, {
        onMouseEnter: open,
        onMouseLeave: close,
        onFocus: open,
        onBlur: close,
        onClick: () => onOpenMaterialsDetail(row),
    });

    return (
        <Popover
            opened={opened}
            position="top"
            withArrow
            shadow="md"
            withinPortal
            zIndex={400}
        >
            <Popover.Target>
                {target}
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap={6}>
                    <Badge size="sm" color="red" variant="light" w="fit-content">
                        Missing Materials
                    </Badge>
                    <Text size="sm" fw={700}>
                        {detail.title}
                    </Text>
                    <Text size="xs">
                        {detail.itemLabel}
                    </Text>
                    <Text size="xs">
                        Required by {detail.requiredByLabel}
                    </Text>
                    {detail.materials
                        .slice()
                        .sort((a, b) => b.missing - a.missing)
                        .map((material) => (
                            <Text key={`${detail.key}:${material.name}`} size="xs">
                                {material.name}: missing {Math.ceil(material.missing)} (available {Math.ceil(material.available)} / required {Math.ceil(material.required)})
                            </Text>
                        ))}
                    <Text size="xs" c="dimmed">
                        Click for the full table.
                    </Text>
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}

function ProductionModuleTimelineView({
    groupedVisibleRows,
    collapsedGroupSet,
    isMobile,
    monthBands,
    monthDividers,
    gridTicks,
    weekDividers,
    windowStart,
    windowEnd,
    windowSeconds,
    gridTemplateColumns,
    labelColumnWidth,
    mobileTimelineWidth,
    onToggleGroup,
    onOpenMaterialsDetail,
}: ProductionModuleTimelineViewProps) {
    const timelineColumnsStyle = cssVars({"--timeline-columns": gridTemplateColumns});
    const mobileInnerStyle = cssVars({"--timeline-min-width": `${labelColumnWidth + mobileTimelineWidth}px`});

    const isVisibleInWindow = (segment: GanttSegment): boolean => {
        if (segment.end === null) {
            return segment.start >= windowStart && segment.start <= windowEnd;
        }
        return segment.end > windowStart && segment.start < windowEnd;
    };

    const toPercent = (ts: number): number => ((ts - windowStart) / windowSeconds) * 100;

    const renderHeader = (scope: TimelineScope) => (
        <div className={classes.headerGrid} style={timelineColumnsStyle}>
            <div/>
            <div className={classes.headerTrack}>
                {monthDividers.map((divider) => (
                    <div
                        key={`${scope}-header-month:${divider.key}`}
                        className={classes.monthDivider}
                        style={cssVars({"--left": `${divider.left}%`})}
                    />
                ))}
                {monthBands.map((band) => (
                    <div
                        key={`${scope}-header-band:${band.key}`}
                        className={classes.monthBand}
                        style={cssVars({"--left": `${band.left}%`, "--width": `${band.width}%`})}
                    >
                        <Text size="xs" fw={700} className={classes.monthLabel} truncate>
                            {isMobile || monthBands.length > 10 ? band.mobileLabel : band.label}
                        </Text>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderRow = (row: GanttRow, scope: TimelineScope) => {
        const rowHeight = getRowHeight(row);
        const rowStyle = cssVars({
            "--timeline-columns": gridTemplateColumns,
            "--row-height": `${rowHeight}px`,
        });

        return (
            <div key={`${scope}:${row.entityId}`} className={classes.row} style={rowStyle}>
                <div className={classes.rowLabel}>
                    <Group gap="xs" wrap="nowrap" justify="space-between">
                        <Text fw={600} truncate>{row.name}</Text>
                        <Badge size="xs" color={row.syncStatus === "ok" ? "green" : "red"}>{row.syncStatus}</Badge>
                    </Group>
                    <Text size="xs" c="dimmed">{row.entityTypeName} - {row.entityId}</Text>
                    <Text size="xs" c="dimmed">Active ETA: {row.activeEta}</Text>
                    <Text size="xs" c="dimmed">Queue ETA: {row.queueEta}</Text>
                    <Tooltip
                        label={row.materialsStatus.status === "missing"
                            ? `${row.materialsStatus.tooltip} Click for details.`
                            : row.materialsStatus.tooltip}
                        withArrow
                    >
                        <Badge
                            size="xs"
                            color={row.materialsStatus.color}
                            variant="light"
                            className={row.materialsStatus.status === "missing" ? classes.materialsBadgeButton : undefined}
                            component={row.materialsStatus.status === "missing" ? "button" : "div"}
                            onClick={row.materialsStatus.status === "missing" ? () => onOpenMaterialsDetail(row) : undefined}
                        >
                            {row.materialsStatus.label}
                        </Badge>
                    </Tooltip>
                </div>
                <div className={classes.track}>
                    {gridTicks.map((tick) => (
                        <div
                            key={`${scope}:${row.entityId}:tick:${tick.key}`}
                            className={classes.rowTick}
                            style={cssVars({"--left": `${tick.left}%`})}
                        />
                    ))}
                    {weekDividers.map((divider) => (
                        <div
                            key={`${scope}:${row.entityId}:week:${divider.key}`}
                            className={classes.weekDivider}
                            style={cssVars({"--left": `${divider.left}%`})}
                        />
                    ))}
                    {monthDividers.map((divider) => (
                        <div
                            key={`${scope}:${row.entityId}:month:${divider.key}`}
                            className={classes.monthDivider}
                            style={cssVars({"--left": `${divider.left}%`})}
                        />
                    ))}
                    {row.materialsStatus.details
                        .filter((detail) => detail.requiredByAt !== null)
                        .filter((detail) => (detail.requiredByAt ?? 0) >= windowStart && (detail.requiredByAt ?? 0) <= windowEnd)
                        .map((detail) => {
                            const left = toPercent(detail.requiredByAt ?? windowStart);
                            return (
                                <MaterialsDeadlinePopover
                                    key={`${scope}:${row.entityId}:materials-deadline:${detail.key}`}
                                    detail={detail}
                                    row={row}
                                    onOpenMaterialsDetail={onOpenMaterialsDetail}
                                >
                                    <div
                                        className={classes.materialsDeadlineButton}
                                        style={cssVars({"--left": `${left}%`})}
                                        aria-label={`Missing materials due by ${detail.requiredByLabel}`}
                                    >
                                        <div className={classes.materialsDeadlineLine}/>
                                        <WarningCircleIcon size={16} color="red" weight="fill"/>
                                    </div>
                                </MaterialsDeadlinePopover>
                            );
                        })}
                    {row.segments.filter(isVisibleInWindow).map((segment) => {
                        const segmentTop = `${6 + segment.lane * 22}px`;
                        if (segment.end === null) {
                            const left = toPercent(segment.start);
                            const markerClassName = segment.kind === "queue_unknown_planned"
                                ? `${classes.unknownMarker} ${classes.unknownMarkerPlanned}`
                                : classes.unknownMarker;
                            return (
                                <SegmentPopover key={segment.key} segment={segment}>
                                    <div className={markerClassName} style={cssVars({"--left": `${left}%`, "--top": segmentTop})}>
                                        {segment.missingDatacard ? (
                                            <Icon icon={IconMap.Datacard} size={10} color="var(--mantine-color-red-9)"/>
                                        ) : null}
                                    </div>
                                </SegmentPopover>
                            );
                        }

                        const clampedStart = Math.max(segment.start, windowStart);
                        const clampedEnd = Math.min(segment.end, windowEnd);
                        const left = toPercent(clampedStart);
                        const width = Math.max(0.9, toPercent(clampedEnd) - toPercent(clampedStart));
                        const minEnd = segment.minEnd ?? null;
                        const minWidth = minEnd && minEnd > clampedStart
                            ? Math.max(0.9, toPercent(Math.min(minEnd, windowEnd)) - toPercent(clampedStart))
                            : 0;
                        const retoolEnd = segment.retoolEnd ?? null;
                        const retoolWidth = retoolEnd && retoolEnd > clampedStart
                            ? toPercent(Math.min(retoolEnd, windowEnd)) - toPercent(clampedStart)
                            : 0;
                        const segmentClassName = segment.kind === "current"
                            ? `${classes.segment} ${classes.segmentCurrent}`
                            : segment.kind === "current_planned"
                                ? `${classes.segment} ${classes.segmentCurrentPlanned}`
                                : segment.kind === "planned"
                                    ? `${classes.segment} ${classes.segmentPlanned}`
                                    : segment.kind === "queue_planned_estimate"
                                        ? `${classes.segment} ${classes.segmentPlannedEstimate}`
                                        : segment.kind === "queue_estimate"
                                            ? `${classes.segment} ${classes.segmentEstimate}`
                                            : segment.kind === "queue_planned"
                                                ? `${classes.segment} ${classes.segmentQueuePlanned}`
                                                : `${classes.segment} ${classes.segmentQueue}`;

                        return (
                            <SegmentPopover key={segment.key} segment={segment}>
                                <div
                                    className={classes.segmentWrapper}
                                    style={cssVars({"--left": `${left}%`, "--width": `${width}%`, "--top": segmentTop})}
                                >
                                    <div className={segmentClassName}>
                                        {(segment.kind === "queue_estimate" || segment.kind === "queue_planned_estimate") && minWidth > 0 ? (
                                            <div
                                                className={classes.estimateMinOverlay}
                                                style={cssVars({"--overlay-width": `${(minWidth / width) * 100}%`})}
                                            />
                                        ) : null}
                                        {retoolWidth > 0 ? (
                                            <div
                                                className={classes.retoolOverlay}
                                                style={cssVars({"--overlay-width": `${(retoolWidth / width) * 100}%`})}
                                            />
                                        ) : null}
                                        <span className={classes.segmentText}>{segment.label}</span>
                                    </div>
                                    {segment.missingDatacard ? (
                                        <Tooltip label="No datacard assigned for this product" withArrow>
                                            <span className={classes.datacardMissingMarker}>
                                                <Icon icon={IconMap.Datacard} size={9} color="#fff"/>
                                            </span>
                                        </Tooltip>
                                    ) : null}
                                </div>
                            </SegmentPopover>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderGroupSection = (section: GanttGroupSection, scope: TimelineScope) => {
        const isCollapsed = collapsedGroupSet.has(section.key);

        return (
            <div key={`${scope}:section:${section.key}`} className={classes.groupSection}>
                <UnstyledButton className={classes.groupTitleButton} onClick={() => onToggleGroup(section.key)}>
                    <div className={classes.groupTitle}>
                        <Group gap={6} wrap="nowrap">
                            {isCollapsed ? <CaretRightIcon size={14}/> : <CaretDownIcon size={14}/>}
                            <Text size="sm" fw={700}>{section.name}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">{section.rows.length} entities</Text>
                    </div>
                </UnstyledButton>
                {!isCollapsed ? section.rows.map((row) => renderRow(row, scope)) : null}
            </div>
        );
    };

    if (isMobile) {
        return (
            <ScrollArea>
                <div className={classes.mobileInner} style={mobileInnerStyle}>
                    {renderHeader("mobile")}
                    {groupedVisibleRows.map((section) => renderGroupSection(section, "mobile"))}
                </div>
            </ScrollArea>
        );
    }

    return (
        <div>
            {renderHeader("desktop")}
            {groupedVisibleRows.map((section) => renderGroupSection(section, "desktop"))}
        </div>
    );
}

export default ProductionModuleTimelineView;
